import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Alert,
  Input as AntInput
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  TagsOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Panel, PanelBody } from "../../components/panel/panel";
import APIs from "../../Constants/apis";
import dayjs from 'dayjs';
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import ProductVariantMultiSelectionModal from "../../components/CustomComponents/modal/productVariantMultiSelectionModal";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useProfileAndSettings from "../../hooks/useProfileAndSettings";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "./rules.css";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const { Option } = Select;
const { Search } = AntInput;

const LOYALTY_RULE_TYPES = [
  {
    value: "AMOUNT",
    title: "Amount Based",
    shortLabel: "Spend amount",
    description: "Earn points for every amount spent (e.g. £1 = X points).",
    Icon: DollarOutlined,
  },
  {
    value: "ORDER",
    title: "Order Based",
    shortLabel: "Per order",
    description: "Award a fixed number of points for every completed order.",
    Icon: ShopOutlined,
  },
  {
    value: "ITEM",
    title: "Item Based",
    shortLabel: "Specific items",
    description: "Set custom points for individual products or variants.",
    Icon: TagsOutlined,
  },
  {
    value: "CATEGORY",
    title: "Category Based",
    shortLabel: "By category",
    description: "Award points for purchases within selected categories.",
    Icon: AppstoreOutlined,
  },
];

const getItemRecordId = (item) =>
  item?.MenuItem_ID ?? item?.menuItem_ID ?? item?.id;

const getVariantParentIdFromRecord = (item) => {
  const raw =
    item?.menuitem_VariantParentID ??
    item?.Menuitem_VariantParentID ??
    item?.menuItem_VariantParentID ??
    item?.parentMenuItem_ID ??
    item?.ParentMenuItem_ID ??
    0;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const idsEqualLoose = (a, b) => {
  if (a == null || b == null) return false;
  if (a == b) return true;
  if (parseInt(a, 10) === parseInt(b, 10)) return true;
  return String(a).trim() === String(b).trim();
};

const buildLoyaltyItemDisplayName = (item, parentCatalog = []) => {
  if (item?.displayName) return item.displayName;
  const name = item?.MenuItem_Name || item?.menuItem_Name || item?.name || "";
  const parentId = getVariantParentIdFromRecord(item);
  if (parentId > 0) {
    const parent = parentCatalog.find((p) =>
      idsEqualLoose(getItemRecordId(p), parentId)
    );
    const parentName = parent?.MenuItem_Name || parent?.menuItem_Name || parent?.name;
    if (parentName && name) return `${parentName} (${name})`;
    if (name) return name;
  }
  return name || `Item #${getItemRecordId(item) || "?"}`;
};

const getItemImageUrl = (item) =>
  item?.menuItem_Image ||
  item?.MenuItem_Image ||
  item?.parentMenuItem_Image ||
  "/assets/img/default-product.png";

const LoyaltyRulesManagement = () => {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [conditionModalVisible, setConditionModalVisible] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [form] = Form.useForm();
  const [conditionForm] = Form.useForm();
  const [ruleType, setRuleType] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [toggles, setToggles] = useState({
    Status: true,
  });
  const watchedSpendAmount = Form.useWatch("SpendAmount", form);
  const watchedRewardPoints = Form.useWatch("RewardPoints", form);
  const watchedExpirationDays = Form.useWatch("ExpirationDays", form);

  // Modal and selector states
  const [modals, setModals] = useState({
    showCategoryModal: false,
    showItemModal: false,
  });
  const [selectors, setSelectors] = useState({
    selectedCategories: [],
    selectedItems: [],
  });
  const [itemLabelCache, setItemLabelCache] = useState({});

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  
  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const compCurrency = getCompCurrency();
  const currencySymbol = getCurrencySymbol(compCurrency);

  useEffect(() => {
    loadRule();
    loadCategories();
  }, []);

  // Cleanup modal styles on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Populate form fields when rule data is loaded
  useEffect(() => {
    if (rule) {
      const currentRuleType = rule.ruleType || rule.RuleType;
      // For ORDER type, set SpendAmount to 1 by default
      const spendAmount = currentRuleType === 'ORDER' ? 1 : (rule.spendAmount || rule.SpendAmount);
      
      form.setFieldsValue({
        RuleType: currentRuleType,
        SpendAmount: spendAmount,
        RewardPoints: rule.rewardPoints || rule.RewardPoints,
        ExpirationDays: rule.expirationDays || rule.ExpirationDays,
        Status: rule.status !== undefined ? rule.status : rule.Status,
      });
      setToggles(prev => ({ ...prev, Status: rule.status !== undefined ? rule.status : rule.Status }));
      setRuleType(currentRuleType);
      setConditions(rule.conditions || rule.Conditions || []);
    } else {
      // Reset form when no rule exists
      form.resetFields();
      form.setFieldsValue({ Status: true });
      setToggles(prev => ({ ...prev, Status: true }));
      setRuleType(null);
      setConditions([]);
      setSelectors({ selectedCategories: [], selectedItems: [] });
    }
  }, [rule, form]);

  // Load selected items/categories when rule, items, and categories are all available
  useEffect(() => {
    if (!rule) return;

    const currentRuleType = rule.ruleType || rule.RuleType;
    const ruleConditions = rule.conditions || rule.Conditions || [];

    if (!ruleConditions.length) {
      setSelectors({ selectedCategories: [], selectedItems: [] });
      setItemLabelCache({});
      return;
    }

    if (currentRuleType === "CATEGORY" && categories.length > 0) {
      setSelectors((prev) => {
        const currentConditionIds = ruleConditions
          .map((c) => parseInt(c.ItemORCategoryID || c.itemORCategoryID, 10))
          .sort();
        const prevCategoryIds = prev.selectedCategories
          .map((c) => parseInt(c.RootCat_ID || c.rootCat_ID || c.id, 10))
          .sort();

        const idsMatch =
          JSON.stringify(currentConditionIds) === JSON.stringify(prevCategoryIds);
        if (idsMatch && prev.selectedCategories.length > 0) {
          return prev;
        }

        const selectedCategories = ruleConditions
          .map((condition) => {
            const conditionId = condition.ItemORCategoryID || condition.itemORCategoryID;
            const pointsPerItem = condition.PointsPerItem || condition.pointsPerItem;
            const expiryDays =
              condition.ExpiryDays !== undefined ? condition.ExpiryDays : condition.expiryDays;

            const category = categories.find((c) => {
              const categoryId = c.RootCat_ID || c.rootCat_ID || c.id;
              return idsEqualLoose(categoryId, conditionId);
            });

            if (!category) return null;
            return {
              ...category,
              pointsPerItem: pointsPerItem || 1,
              expiryDays: expiryDays !== undefined ? expiryDays : null,
            };
          })
          .filter(Boolean);

        if (selectedCategories.length > 0) {
          return { ...prev, selectedCategories };
        }
        return prev;
      });
      return;
    }

    if (currentRuleType !== "ITEM" || !CompID) {
      return;
    }

    let cancelled = false;

    const mapConditionsToSelectedItems = async () => {
      const currentConditionIds = ruleConditions
        .map((c) => parseInt(c.ItemORCategoryID || c.itemORCategoryID, 10))
        .filter((id) => Number.isFinite(id))
        .sort();

      const unmatchedIds = [];
      const partialMap = new Map();

      ruleConditions.forEach((condition) => {
        const conditionId = condition.ItemORCategoryID || condition.itemORCategoryID;
        const pointsPerItem = condition.PointsPerItem || condition.pointsPerItem;
        const expiryDays =
          condition.ExpiryDays !== undefined ? condition.ExpiryDays : condition.expiryDays;

        const parentMatch = items.find((i) =>
          idsEqualLoose(getItemRecordId(i), conditionId)
        );

        if (parentMatch) {
          partialMap.set(String(conditionId), {
            ...parentMatch,
            displayName: buildLoyaltyItemDisplayName(parentMatch, items),
            pointsPerItem: pointsPerItem || 1,
            expiryDays: expiryDays !== undefined ? expiryDays : null,
          });
        } else {
          unmatchedIds.push(parseInt(conditionId, 10));
        }
      });

      let fetchedItems = [];
      if (unmatchedIds.length > 0) {
        try {
          const response = await axiosInstance.post(APIs.POST_BULK_GET_ITEMS, {
            CompID,
            MenuItemIds: unmatchedIds,
          });
          const ok = response.data?.status || response.data?.Status;
          const data = response.data?.data || response.data?.Data;
          if (ok && data) {
            fetchedItems = data.items || data.Items || [];
          }

          const variantsNeedingParent = fetchedItems.filter((row) => {
            const type = (row.menuItem_Type || row.MenuItem_Type || "").toLowerCase();
            return type === "variant" && getVariantParentIdFromRecord(row) <= 0;
          });

          if (variantsNeedingParent.length > 0) {
            const enriched = await Promise.all(
              variantsNeedingParent.map(async (row) => {
                try {
                  const detailRes = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
                    params: { CompID, MenuItem_ID: getItemRecordId(row) },
                  });
                  const detail = detailRes.data?.data || detailRes.data?.Data;
                  return detail ? { ...row, ...detail } : row;
                } catch {
                  return row;
                }
              })
            );
            const enrichedById = new Map(
              enriched.map((row) => [String(getItemRecordId(row)), row])
            );
            fetchedItems = fetchedItems.map((row) => {
              const key = String(getItemRecordId(row));
              return enrichedById.get(key) || row;
            });
          }
        } catch (error) {
          console.error("Error bulk-fetching loyalty rule items:", error);
        }
      }

      if (cancelled) return;
      if (fetchedItems.length) {
        mergeItemsCache(fetchedItems);
      }

      const labelUpdates = {};
      const selectedItems = ruleConditions
        .map((condition) => {
          const conditionId = condition.ItemORCategoryID || condition.itemORCategoryID;
          const pointsPerItem = condition.PointsPerItem || condition.pointsPerItem;
          const expiryDays =
            condition.ExpiryDays !== undefined ? condition.ExpiryDays : condition.expiryDays;

          if (partialMap.has(String(conditionId))) {
            const mapped = partialMap.get(String(conditionId));
            labelUpdates[conditionId] = mapped.displayName;
            return mapped;
          }

          const fetched = fetchedItems.find((i) =>
            idsEqualLoose(getItemRecordId(i), conditionId)
          );
          if (fetched) {
            const entry = {
              ...fetched,
              displayName: buildLoyaltyItemDisplayName(fetched, items),
              pointsPerItem: pointsPerItem || 1,
              expiryDays: expiryDays !== undefined ? expiryDays : null,
            };
            labelUpdates[conditionId] = entry.displayName;
            return entry;
          }

          labelUpdates[conditionId] = `Item #${conditionId}`;
          return null;
        })
        .filter(Boolean);

      setItemLabelCache(labelUpdates);

      setSelectors((prev) => {
        const prevItemIds = prev.selectedItems
          .map((i) => parseInt(getItemRecordId(i), 10))
          .sort();
        const idsMatch =
          JSON.stringify(currentConditionIds) === JSON.stringify(prevItemIds);
        if (idsMatch && prev.selectedItems.length > 0) {
          return prev;
        }
        if (selectedItems.length > 0) {
          return { ...prev, selectedItems };
        }
        return prev;
      });
    };

    mapConditionsToSelectedItems();

    return () => {
      cancelled = true;
    };
  }, [rule, items, categories, CompID]);

  const loadRule = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_LOYALTY_RULE}?compID=${CompID}`
      );

      // Handle both Status and status (case-insensitive)
      const isSuccess = response.data.status || response.data.Status;
      const ruleData = response.data.data || response.data.Data;

      console.log("ruleData", ruleData);

      if (isSuccess && ruleData) {
        // Check if rule exists (Rule_ID > 0) - handle both cases
        const ruleId = ruleData.rule_ID || ruleData.Rule_ID;
        if (ruleId && ruleId > 0) {
          setRule(ruleData);
          setIsEditMode(false);
        } else {
          // No rule yet — show setup empty state until user enables loyalty
          setRule(null);
          setIsEditMode(false);
        }
      } else {
        setRule(null);
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error loading rule:", error);
      showErrorToast("Error loading loyalty rule");
      setRule(null);
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const mergeItemsCache = (list) => {
    if (!Array.isArray(list) || !list.length) return;
    setItems((prev) => {
      const map = new Map(
        (prev || []).map((i) => [String(getItemRecordId(i)), i])
      );
      list.forEach((item) => {
        const id = getItemRecordId(item);
        if (id == null) return;
        map.set(String(id), { ...map.get(String(id)), ...item });
      });
      return Array.from(map.values());
    });
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get(
        `${APIs.GET_CATEGORIES}?CompID=${CompID}&SortBy=rootCat_Orderby&SortOrder=ASC&Search=`
      );
     
      console.log("Categories API response:", response.data);
      
      if (response.data.status || response.data.Status) {
        const categoriesData = response.data.data || response.data.Data || [];
        console.log("Loaded categories:", categoriesData.length, "categories");
        setCategories(categoriesData);
      } else {
        console.warn("Unexpected categories response structure:", response.data);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories([]);
    }
  };

  const handleEnableLoyalty = () => {
    form.resetFields();
    setRuleType(null);
    setConditions([]);
    setSelectors({ selectedCategories: [], selectedItems: [] });
    setItemLabelCache({});
    form.setFieldsValue({ Status: true });
    setToggles((prev) => ({ ...prev, Status: true }));
    setIsEditMode(true);
  };

  const handleRuleTypeChange = (value) => {
    setRuleType(value);
    setConditions([]);
    setSelectors({ selectedCategories: [], selectedItems: [] });
    if (value === "ORDER") {
      form.setFieldsValue({
        RuleType: value,
        SpendAmount: 1,
        RewardPoints: null,
        ExpirationDays: null,
      });
    } else {
      form.setFieldsValue({
        RuleType: value,
        SpendAmount: null,
        RewardPoints: null,
        ExpirationDays: null,
      });
    }
  };

  const handleEdit = () => {
    if (rule) {
      const currentRuleType = rule.ruleType || rule.RuleType;
      const spendAmount = currentRuleType === 'ORDER' ? 1 : (rule.spendAmount || rule.SpendAmount);
      
      form.setFieldsValue({
        RuleType: currentRuleType,
        SpendAmount: spendAmount,
        RewardPoints: rule.rewardPoints || rule.RewardPoints,
        ExpirationDays: rule.expirationDays || rule.ExpirationDays,
        Status: rule.status !== undefined ? rule.status : rule.Status,
      });
      setToggles(prev => ({ ...prev, Status: rule.status !== undefined ? rule.status : rule.Status }));
      setRuleType(currentRuleType);
      setConditions(rule.conditions || rule.Conditions || []);
      setIsEditMode(true);
    }
  };

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    const vals = form.getFieldsValue(true);
    return JSON.stringify({
      ruleType: ruleType || null,
      toggles,
      form: vals,
      conditions,
      selectedItems: selectors.selectedItems,
      selectedCategories: selectors.selectedCategories,
      watchedSpendAmount,
      watchedRewardPoints,
      watchedExpirationDays,
    });
  }, [form, ruleType, toggles, conditions, selectors.selectedItems, selectors.selectedCategories, watchedSpendAmount, watchedRewardPoints, watchedExpirationDays]);

  useEffect(() => {
    if (!isEditMode) {
      initialFormSnapshotRef.current = null;
      return;
    }
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [isEditMode, getFormSnapshot]);

  const isDirty =
    isEditMode &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return false;
    }

    try {
      // Validate that items/categories are selected for ITEM/CATEGORY rules
      if (values.RuleType === 'ITEM' && selectors.selectedItems.length === 0) {
        showErrorToast('Please select at least one item for the loyalty rule');
        return false;
      }
      if (values.RuleType === 'CATEGORY' && selectors.selectedCategories.length === 0) {
        showErrorToast('Please select at least one category for the loyalty rule');
        return false;
      }

      let conditions = [];

      // Add selected items/categories as conditions for ITEM/CATEGORY rules
      if (values.RuleType === 'ITEM') {
        conditions = selectors.selectedItems.map(item => {
          const itemId = getItemRecordId(item);
          return {
            ItemORCategoryID: itemId,
            PointsPerItem: item.pointsPerItem || 1,
            ExpiryDays: item.expiryDays || null
          };
        });
      } else if (values.RuleType === 'CATEGORY') {
        conditions = selectors.selectedCategories.map(category => {
          const categoryId = category.RootCat_ID || category.rootCat_ID || category.id;
          return {
            ItemORCategoryID: categoryId,
            PointsPerItem: category.pointsPerItem || 1,
            ExpiryDays: category.expiryDays || null
          };
        });
      }

      // For ORDER type, ensure SpendAmount is set to 1
      const spendAmount = values.RuleType === 'ORDER' ? 1 : values.SpendAmount;
      
      const payload = {
        CompID: CompID,
        RuleType: values.RuleType,
        SpendAmount: spendAmount,
        RewardPoints: values.RewardPoints,
        ExpirationDays: values.ExpirationDays || null,
        Status: values.Status !== undefined ? values.Status : true,
        Conditions: conditions
      };

      const response = await axiosInstance.post(
        `${APIs.UPSERT_LOYALTY_RULE}`,
        payload
      );

      if (response.data.status || response.data.Status) {
        const action = rule ? 'updated' : 'created';
        showSuccessToast('Loyalty rule ' + action + ' successfully');
        loadRule();
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          setIsEditMode(false); // Save and Exit → view mode
        }
        return true;
      } else {
        const action = rule ? 'update' : 'create';
        showErrorToast(response.data.Message || 'Failed to ' + action + ' loyalty rule');
        return false;
      }
    } catch (error) {
      console.error("Error saving rule:", error);
      const action = rule ? 'updating' : 'creating';
      showErrorToast('Error ' + action + ' loyalty rule');
      return false;
    }
  };

  const handleAddCondition = () => {
    setEditingCondition(null);
    conditionForm.resetFields();
    setConditionModalVisible(true);
  };

  const handleEditCondition = (condition) => {
    setEditingCondition(condition);
    conditionForm.setFieldsValue({
      ItemORCategoryID: condition.ItemORCategoryID,
      PointsPerItem: condition.PointsPerItem,
      ExpiryDays: condition.ExpiryDays || null,
    });
    setConditionModalVisible(true);
  };

  const handleDeleteCondition = async (conditionId) => {
    try {
      const condition = conditions.find(c => c.Condition_ID === conditionId);

      if (!condition) return;

      const response = await axiosInstance.delete(
        `${APIs.DELETE_LOYALTY_RULE_CONDITION}?compID=${CompID}&itemORCategoryID=${condition.ItemORCategoryID}`
      );

      if (response.data.Status) {
        showSuccessToast("Condition deleted successfully");
        loadRule();
      } else {
        showErrorToast(response.data.Message || "Failed to delete condition");
      }
    } catch (error) {
      console.error("Error deleting condition:", error);
      showErrorToast("Error deleting condition");
    }
  };

  const handleConditionSubmit = async (values) => {
    try {
      // Check for duplicate (if editing, allow same ID, otherwise check for duplicates)
      if (!editingCondition) {
        const existing = conditions.find(c => c.ItemORCategoryID === values.ItemORCategoryID);
        if (existing) {
          showErrorToast((ruleType === 'ITEM' ? 'Item' : 'Category') + ' already exists in conditions');
          return;
        }
      }

      const conditionData = {
        CompID: CompID,
        ItemORCategoryID: values.ItemORCategoryID,
        PointsPerItem: values.PointsPerItem,
        ExpiryDays: values.ExpiryDays || null
      };

      const response = await axiosInstance.post(
        `${APIs.UPSERT_LOYALTY_RULE_CONDITION}`,
        conditionData
      );

      if (response.data.Status) {
        showSuccessToast("Condition saved successfully");
        setConditionModalVisible(false);
        conditionForm.resetFields();
        setEditingCondition(null);
        loadRule();
      } else {
        showErrorToast(response.data.Message || "Failed to save condition");
      }
    } catch (error) {
      console.error("Error saving condition:", error);
      showErrorToast("Error saving condition");
    }
  };

  const getItemName = (id) => {
    if (itemLabelCache[id]) return itemLabelCache[id];
    const fromSelector = selectors.selectedItems.find((i) =>
      idsEqualLoose(getItemRecordId(i), id)
    );
    if (fromSelector) return buildLoyaltyItemDisplayName(fromSelector, items);
    const item = items.find((i) => idsEqualLoose(getItemRecordId(i), id));
    if (item) return buildLoyaltyItemDisplayName(item, items);
    return `Item #${id}`;
  };

  const getCategoryName = (id) => {
    const category = categories.find(c => c.RootCat_ID === id);
    return category ? category.RootCat_Name : `Category #${id}`;
  };

  // Handle selector submissions
  const handleSelectorSubmit = (key) => (selectedItems) => {
    console.log("handleSelectorSubmit called with key:", key, "selectedItems:", selectedItems);
    if (key === "selectedCategories") {
      // For categories, selectedItems should already be full objects
      const processedCategories = (selectedItems || []).map((cat) => {
        // If it's just an ID, find the full category object
        if (typeof cat === 'number' || (typeof cat === 'string' && !isNaN(cat))) {
          const categoryId = parseInt(cat);
          const fullCategory = categories.find(c => 
            (c.RootCat_ID || c.rootCat_ID || c.id) == categoryId
          );
          if (fullCategory) {
            return {
              ...fullCategory,
              pointsPerItem: fullCategory.pointsPerItem || 1,
              expiryDays: fullCategory.expiryDays || null,
            };
          }
          return null;
        }
        // If it's already an object, just ensure it has the required fields
        return {
          ...cat,
          pointsPerItem: cat.pointsPerItem || 1,
          expiryDays: cat.expiryDays || null,
        };
      }).filter(Boolean);
      
      setSelectors((prev) => ({ 
        ...prev, 
        selectedCategories: processedCategories 
      }));
    } else if (key === "selectedItems") {
      const processedItems = (selectedItems || []).map((item) => {
        if (typeof item === "number" || (typeof item === "string" && !isNaN(item))) {
          const itemId = parseInt(item, 10);
          const existingItem = selectors.selectedItems.find((sel) =>
            idsEqualLoose(getItemRecordId(sel), itemId)
          );
          const fullItem = items.find((i) => idsEqualLoose(getItemRecordId(i), itemId));
          if (fullItem) {
            return {
              ...fullItem,
              displayName: buildLoyaltyItemDisplayName(fullItem, items),
              pointsPerItem: existingItem?.pointsPerItem || 1,
              expiryDays: existingItem?.expiryDays ?? null,
            };
          }
          return existingItem || null;
        }

        const itemId = getItemRecordId(item);
        const existingItem = selectors.selectedItems.find((sel) =>
          idsEqualLoose(getItemRecordId(sel), itemId)
        );
        const parentCatalog = items;
        const displayName =
          item.displayName || buildLoyaltyItemDisplayName(item, parentCatalog);

        if (item.parentMenuItem_ID || item.ParentMenuItem_ID || getVariantParentIdFromRecord(item) > 0) {
          return {
            ...item,
            menuItem_ID: itemId,
            MenuItem_ID: itemId,
            displayName,
            pointsPerItem: item.pointsPerItem ?? existingItem?.pointsPerItem ?? 1,
            expiryDays: item.expiryDays !== undefined ? item.expiryDays : existingItem?.expiryDays ?? null,
          };
        }

        const fullItem = items.find((i) => idsEqualLoose(getItemRecordId(i), itemId));
        if (fullItem) {
          return {
            ...fullItem,
            displayName: buildLoyaltyItemDisplayName(fullItem, items),
            pointsPerItem: item.pointsPerItem ?? existingItem?.pointsPerItem ?? 1,
            expiryDays: item.expiryDays !== undefined ? item.expiryDays : existingItem?.expiryDays ?? null,
          };
        }

        return {
          ...item,
          displayName,
          pointsPerItem: item.pointsPerItem || 1,
          expiryDays: item.expiryDays ?? null,
        };
      }).filter(Boolean);

      const labelUpdates = {};
      processedItems.forEach((item) => {
        const id = getItemRecordId(item);
        if (id != null) {
          labelUpdates[id] = buildLoyaltyItemDisplayName(item, items);
        }
      });
      setItemLabelCache((prev) => ({ ...prev, ...labelUpdates }));

      setSelectors((prev) => ({
        ...prev,
        selectedItems: processedItems,
      }));
    }
    closeAllModals();
  };

  // Modal management
  const openModal = (modalName) => {
    console.log("Opening modal:", modalName);
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = key === modalName;
      });
      console.log("New modal state:", newModals);
      return newModals;
    });
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
  };

  const closeAllModals = () => {
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = false;
      });
      return newModals;
    });
    document.body.style.overflow = 'unset';
    document.body.classList.remove('modal-open');
  };

  // Handle item/category updates
  const handleItemUpdate = (itemId, field, value) => {
    console.log("Updating item:", itemId, "field:", field, "value:", value);
    setSelectors((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.map((item) => {
        const currentId = item.MenuItem_ID || item.menuItem_ID || item.id;
        // Use strict comparison with type conversion to ensure accurate matching
        const currentIdNum = parseInt(currentId);
        const itemIdNum = parseInt(itemId);
        const isMatch = currentIdNum === itemIdNum || String(currentId).trim() === String(itemId).trim();
        console.log("Comparing:", currentId, "with:", itemId, "match:", isMatch);
        return isMatch ? { ...item, [field]: value } : item;
      }),
    }));
  };

  const handleCategoryUpdate = (categoryId, field, value) => {
    console.log("Updating category:", categoryId, "field:", field, "value:", value);
    setSelectors((prev) => ({
      ...prev,
      selectedCategories: prev.selectedCategories.map((category) => {
        const currentId = category.RootCat_ID || category.rootCat_ID || category.id;
        // Use strict comparison with type conversion to ensure accurate matching
        const currentIdNum = parseInt(currentId);
        const categoryIdNum = parseInt(categoryId);
        const isMatch = currentIdNum === categoryIdNum || String(currentId).trim() === String(categoryId).trim();
        console.log("Comparing:", currentId, "with:", categoryId, "match:", isMatch);
        return isMatch ? { ...category, [field]: value } : category;
      }),
    }));
  };

  const removeItem = (itemId) => {
    console.log("Removing item:", itemId);
    setSelectors((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.filter((item) => {
        const currentId = item.MenuItem_ID || item.menuItem_ID || item.id;
        // Use strict comparison with type conversion
        const currentIdNum = parseInt(currentId);
        const itemIdNum = parseInt(itemId);
        const shouldKeep = currentIdNum !== itemIdNum && String(currentId).trim() !== String(itemId).trim();
        console.log("Checking item ID:", currentId, "against:", itemId, "keep:", shouldKeep);
        return shouldKeep;
      }),
    }));
  };

  const removeCategory = (categoryId) => {
 
    setSelectors((prev) => ({
      ...prev,
      selectedCategories: prev.selectedCategories.filter((category) => {
        const currentId = category.RootCat_ID || category.rootCat_ID || category.id;
        // Use strict comparison with type conversion
        const currentIdNum = parseInt(currentId);
        const categoryIdNum = parseInt(categoryId);
        const shouldKeep = currentIdNum !== categoryIdNum && String(currentId).trim() !== String(categoryId).trim();
        console.log("Checking category ID:", currentId, "against:", categoryId, "keep:", shouldKeep);
        return shouldKeep;
      }),
    }));
  };

  const handleToggle = (field) => (checked) => {
    setToggles(prev => ({ ...prev, [field]: checked }));
    form.setFieldsValue({ [field]: checked });
  };

  const renderSelectionAddButton = (label, onClick) => (
    <button
      type="button"
      className="loyalty-selection-add-btn"
      onClick={onClick}
      style={{
        "--loyalty-accent": themeColor,
        borderColor: themeColor,
        color: themeColor,
      }}
    >
      <PlusOutlined />
      <span>{label}</span>
    </button>
  );

  const renderSelectionEmpty = ({ icon: Icon, title, hint }) => (
    <div className={`loyalty-selection-empty ${isDarkMode ? "loyalty-selection-empty--dark" : ""}`}>
      <Icon style={{ fontSize: "28px", opacity: 0.45, marginBottom: "10px" }} />
      <div className="fw-semibold mb-1">{title}</div>
      <small>{hint}</small>
    </div>
  );

  const renderSelectedItemsEditor = () => (
    <div
      className={`loyalty-selection-block ${isDarkMode ? "loyalty-selection-block--dark" : ""}`}
      style={{ "--loyalty-accent": themeColor }}
    >
      <div className="loyalty-selection-header">
        <div>
          <h6 className="mb-1 d-flex align-items-center loyalty-selection-title">
            <ShopOutlined className="me-2" style={{ color: themeColor }} />
            Selected items
          </h6>
          <small className="text-muted">
            Choose products or specific variants. Points apply only to matching order lines.
          </small>
        </div>
        {renderSelectionAddButton(
          selectors.selectedItems.length > 0 ? "Add more items" : "Select items",
          () => openModal("showItemModal")
        )}
      </div>

      {selectors.selectedItems.length === 0 ? (
        renderSelectionEmpty({
          icon: ShopOutlined,
          title: "No items selected",
          hint: "Click Select items to add products or size variants to this rule.",
        })
      ) : (
        <div className="table-responsive loyalty-selection-table-wrap">
          <table className={`table table-hover loyalty-selection-table ${isDarkMode ? "loyalty-selection-table--dark" : ""}`}>
            <thead>
              <tr>
                <th>Product</th>
                <th style={{ width: "140px" }}>Points earned</th>
                <th style={{ width: "140px" }}>Expiration (days)</th>
                <th style={{ width: "56px" }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {selectors.selectedItems.map((item, index) => {
                const itemId = getItemRecordId(item);
                const itemName =
                  item.displayName || buildLoyaltyItemDisplayName(item, items);
                const isVariant =
                  getVariantParentIdFromRecord(item) > 0 ||
                  item.parentMenuItem_ID ||
                  item.ParentMenuItem_ID;

                return (
                  <tr key={`item-${itemId}-${index}`}>
                    <td>
                      <div className="loyalty-selection-product-cell">
                        <div className="loyalty-selection-product-thumb">
                          <LazyLoadImage
                            src={getItemImageUrl(item)}
                            alt={itemName}
                            effect="opacity"
                          />
                        </div>
                        <div className="loyalty-selection-product-text">
                          <span className="loyalty-selection-product-name">{itemName}</span>
                          {isVariant && (
                            <span className="loyalty-selection-product-badge">Variant</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        value={item.pointsPerItem ?? 1}
                        onChange={(value) =>
                          handleItemUpdate(itemId, "pointsPerItem", value ?? 1)
                        }
                        size="small"
                        className="loyalty-selection-input w-100"
                      />
                    </td>
                    <td>
                      <InputNumber
                        min={0}
                        value={item.expiryDays ?? null}
                        onChange={(value) =>
                          handleItemUpdate(itemId, "expiryDays", value ?? null)
                        }
                        size="small"
                        placeholder="Optional"
                        className="loyalty-selection-input w-100"
                      />
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="loyalty-selection-remove-btn"
                        onClick={() => removeItem(itemId)}
                        title="Remove item"
                        aria-label={`Remove ${itemName}`}
                      >
                        <DeleteOutlined />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSelectedCategoriesEditor = () => (
    <div
      className={`loyalty-selection-block ${isDarkMode ? "loyalty-selection-block--dark" : ""}`}
      style={{ "--loyalty-accent": themeColor }}
    >
      <div className="loyalty-selection-header">
        <div>
          <h6 className="mb-1 d-flex align-items-center loyalty-selection-title">
            <TagsOutlined className="me-2" style={{ color: themeColor }} />
            Selected categories
          </h6>
          <small className="text-muted">
            Points apply to all products in the chosen categories.
          </small>
        </div>
        {renderSelectionAddButton(
          selectors.selectedCategories.length > 0 ? "Add more categories" : "Select categories",
          () => openModal("showCategoryModal")
        )}
      </div>

      {selectors.selectedCategories.length === 0 ? (
        renderSelectionEmpty({
          icon: TagsOutlined,
          title: "No categories selected",
          hint: "Click Select categories to configure point values per category.",
        })
      ) : (
        <div className="table-responsive loyalty-selection-table-wrap">
          <table className={`table table-hover loyalty-selection-table ${isDarkMode ? "loyalty-selection-table--dark" : ""}`}>
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ width: "140px" }}>Points earned</th>
                <th style={{ width: "140px" }}>Expiration (days)</th>
                <th style={{ width: "56px" }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {selectors.selectedCategories.map((category, index) => {
                const categoryId =
                  category.RootCat_ID || category.rootCat_ID || category.id;
                const categoryName =
                  category.RootCat_Name ||
                  category.rootCat_Name ||
                  category.name ||
                  categories.find((c) =>
                    idsEqualLoose(c.RootCat_ID || c.rootCat_ID || c.id, categoryId)
                  )?.RootCat_Name ||
                  "No Name";

                return (
                  <tr key={`category-${categoryId}-${index}`}>
                    <td>
                      <div className="loyalty-selection-product-cell">
                        <div
                          className="loyalty-selection-product-thumb loyalty-selection-product-thumb--icon"
                          style={{ color: themeColor, borderColor: `${themeColor}40` }}
                        >
                          <TagsOutlined />
                        </div>
                        <span className="loyalty-selection-product-name">{categoryName}</span>
                      </div>
                    </td>
                    <td>
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        value={category.pointsPerItem ?? 1}
                        onChange={(value) =>
                          handleCategoryUpdate(categoryId, "pointsPerItem", value ?? 1)
                        }
                        size="small"
                        className="loyalty-selection-input w-100"
                      />
                    </td>
                    <td>
                      <InputNumber
                        min={0}
                        value={category.expiryDays ?? null}
                        onChange={(value) =>
                          handleCategoryUpdate(categoryId, "expiryDays", value ?? null)
                        }
                        size="small"
                        placeholder="Optional"
                        className="loyalty-selection-input w-100"
                      />
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="loyalty-selection-remove-btn"
                        onClick={() => removeCategory(categoryId)}
                        title="Remove category"
                        aria-label={`Remove ${categoryName}`}
                      >
                        <DeleteOutlined />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const conditionColumns = [
    {
      title: ruleType === 'ITEM' ? "Item" : "Category",
      key: "name",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 p-1 rounded"
            style={{
              backgroundColor: isDarkMode ? '#374151' : '#f0f0f0',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TagsOutlined style={{ color: themeColor }} />
          </div>
          <div>
            <div className="fw-semibold">
              {ruleType === 'ITEM'
                ? getItemName(record.ItemORCategoryID)
                : getCategoryName(record.ItemORCategoryID)}
            </div>
       
          </div>
        </div>
      ),
    },
    {
      title: "Points Per Item",
      dataIndex: "PointsPerItem",
      key: "points",
      render: (points) => (
        <div className="text-center">
          <Tag
            color="blue"
            className="fw-semibold px-3 py-1"
            style={{ fontSize: '14px' }}
          >
            {points} pts
          </Tag>
        </div>
      ),
    },
    {
      title: "Expiry Days",
      dataIndex: "ExpiryDays",
      key: "expiry",
      render: (days) => (
        <div className="text-center">
          {days && days > 0 ? (
            <Tag color="orange" className="px-3 py-1 fw-semibold">
              {days} {days === 1 ? 'day' : 'days'}
            </Tag>
          ) : (
            <Tag color="green" className="px-3 py-1">
              <ClockCircleOutlined className="me-1" />
              Never Expires
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCondition(record)}
            style={{
              backgroundColor: themeColor,
              borderColor: themeColor,
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this condition?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteCondition(record.Condition_ID)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderHeaderActionButton = ({ label, onClick, iconClass }) => (
    <button
      type="button"
      className="btn px-4"
      onClick={onClick}
      style={{
        backgroundColor: themeColor,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
        border: "none",
        color: "white",
        borderRadius: "8px",
        padding: "8px 16px",
        fontSize: "14px",
        fontWeight: "600",
        transition: "all 0.3s ease",
        boxShadow: `0 2px 8px ${themeColor}40`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}60`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
      }}
    >
      {iconClass ? <i className={`${iconClass} me-2`} aria-hidden="true" /> : null}
      {label}
    </button>
  );

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
    enabled: isEditMode,
  });

  const renderBreadcrumb = () => {
    const showHeaderActions = isEditMode || !!rule;
    const subtitle = isEditMode
      ? "Configure how customers earn loyalty points"
      : rule
        ? "View loyalty rule configuration"
        : "Set up loyalty to start rewarding your customers";

    return (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item active">Loyalty Rule</li>
        </ol>
        <h1 className="page-header mb-0">Loyalty Rule</h1>
        <p className="text-muted">{subtitle}</p>
      </div>
      {showHeaderActions && (
      <div className="ms-auto">
        {isEditMode
          ? (
            <SaveActions
              onCancel={() => {
                initialFormSnapshotRef.current = getFormSnapshot();
                setIsEditMode(false);
              }}
              onSave={() => handleSubmit({ exitAfterSave: false })}
              onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
            />
            )
          : renderHeaderActionButton({
              label: "Edit Rule",
              onClick: handleEdit,
              iconClass: "fas fa-pen",
            })}
      </div>
      )}
    </div>
    );
  };

  const renderSetupEmptyState = () => (
    <div
      className={`loyalty-setup-empty ${isDarkMode ? "loyalty-setup-empty--dark" : ""}`}
      style={{ "--loyalty-accent": themeColor }}
    >
      <div className="loyalty-setup-empty__icon" aria-hidden="true">
        <SettingOutlined />
      </div>
      <h3 className="loyalty-setup-empty__title">You have not set up loyalty</h3>
      <p className="loyalty-setup-empty__description">
        Enable loyalty to create a rule and start rewarding customers with points for
        their purchases.
      </p>
      <button
        type="button"
        className="btn px-4"
        onClick={handleEnableLoyalty}
        style={{
          backgroundColor: themeColor,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          border: "none",
          color: "white",
          borderRadius: "8px",
          padding: "10px 20px",
          fontSize: "14px",
          fontWeight: "600",
          boxShadow: `0 2px 8px ${themeColor}40`,
        }}
      >
        <i className="fas fa-plus me-2" aria-hidden="true" />
        Enable Loyalty
      </button>
    </div>
  );

  const renderRuleView = () => {
    if (!rule) {
      return renderSetupEmptyState();
    }

    const currentRuleType = rule.ruleType || rule.RuleType;
    const spendAmount = rule.spendAmount || rule.SpendAmount;
    const rewardPoints = rule.rewardPoints || rule.RewardPoints;
    const expirationDays = rule.expirationDays || rule.ExpirationDays;
    const status = rule.status !== undefined ? rule.status : rule.Status;
    const ruleConditions = rule.conditions || rule.Conditions || [];

    return (
      <div>
        {/* Rule Overview Card */}
        <div className="row mb-4">
          <div className="col-12">
            <Card
              className="border-0 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <div className="card-body p-4">
                <h5 className="card-title mb-4 d-flex align-items-center">
                  <SettingOutlined className="me-2" />
                  Loyalty Rule Overview
                </h5>
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <div className="text-center">
                      <div className="mb-2">
                        <TagsOutlined
                          style={{
                            fontSize: '24px',
                            color: currentRuleType === 'AMOUNT' ? '#1890ff' :
                                   currentRuleType === 'ORDER' ? '#52c41a' :
                                   currentRuleType === 'ITEM' ? '#fa8c16' : '#722ed1'
                          }}
                        />
                      </div>
                      <div className="text-muted small mb-1">Rule Type</div>
                      <Tag
                        color={
                          currentRuleType === 'AMOUNT' ? 'blue' :
                          currentRuleType === 'ORDER' ? 'green' :
                          currentRuleType === 'ITEM' ? 'orange' : 'purple'
                        }
                        className="fw-semibold"
                      >
                        {currentRuleType === 'AMOUNT' ? 'Amount Based' :
                         currentRuleType === 'ORDER' ? 'Order Based' :
                         currentRuleType === 'ITEM' ? 'Item Based' : 'Category Based'}
                      </Tag>
                    </div>
                  </Col>
                  {currentRuleType === 'AMOUNT' && (
                    <Col xs={24} sm={12} md={6}>
                      <div className="text-center">
                        <div className="mb-2">
                          <DollarOutlined
                            style={{ fontSize: '24px', color: '#1890ff' }}
                          />
                        </div>
                        <div className="text-muted small mb-1">Base Amount (X)</div>
                        <div className="fw-semibold fs-5 text-primary">
                          {currencySymbol}{spendAmount}
                        </div>
                      </div>
                    </Col>
                  )}
                  <Col xs={24} sm={12} md={currentRuleType === 'AMOUNT' ? 6 : currentRuleType === 'ORDER' ? 8 : 6}>
                    <div className="text-center">
                      <div className="mb-2">
                        <ShopOutlined
                          style={{ fontSize: '24px', color: '#52c41a' }}
                        />
                      </div>
                      <div className="text-muted small mb-1">
                        {currentRuleType === 'ORDER' ? 'Reward Points per Order' : 'Reward Points (Y)'}
                      </div>
                      <div className="fw-semibold fs-5 text-success">{rewardPoints} pts</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={currentRuleType === 'AMOUNT' ? 6 : currentRuleType === 'ORDER' ? 8 : 6}>
                    <div className="text-center">
                      <div className="mb-2">
                        {status ?
                          <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} /> :
                          <StopOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                        }
                      </div>
                      <div className="text-muted small mb-1">Status</div>
                      <Tag color={status ? "success" : "error"} className="fw-semibold">
                        {status ? "Active" : "Inactive"}
                      </Tag>
                    </div>
                  </Col>
                  {expirationDays && (
                    <Col xs={24} sm={12} md={6}>
                      <div className="text-center">
                        <div className="mb-2">
                          <ClockCircleOutlined
                            style={{ fontSize: '24px', color: '#fa8c16' }}
                          />
                        </div>
                        <div className="text-muted small mb-1">Point Expiration</div>
                        <div className="fw-semibold">{expirationDays} days</div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </Card>
          </div>
        </div>

        {/* Conditions Table for ITEM/CATEGORY rules */}
        {(currentRuleType === 'ITEM' || currentRuleType === 'CATEGORY') && ruleConditions.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <Card
                title={
                  <div className="d-flex align-items-center">
                    <TagsOutlined className="me-2" />
                    {currentRuleType === 'ITEM' ? 'Item Conditions' : 'Category Conditions'}
                  </div>
                }
                className="border-0 shadow-sm"
                style={{
                  backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                  border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                  color: isDarkMode ? '#ffffff' : '#000000',
                }}
              >
                <Table
                  columns={[
                    {
                      title: <div className="text-center">{currentRuleType === 'ITEM' ? "Item" : "Category"}</div>,
                      key: "name",
                      align: 'center',
                      render: (_, record) => {
                        const itemOrCategoryId = record.ItemORCategoryID || record.itemORCategoryID;
                        let name = '';
                        
                        if (currentRuleType === 'ITEM') {
                          name = getItemName(itemOrCategoryId);
                        } else {
                          const category = categories.find(c => {
                            const categoryId = c.RootCat_ID || c.rootCat_ID || c.id;
                            return parseInt(categoryId) === parseInt(itemOrCategoryId) || String(categoryId).trim() === String(itemOrCategoryId).trim();
                          });
                          name = category ? (category.RootCat_Name || category.rootCat_Name || category.name || `Category #${itemOrCategoryId}`) : `Category #${itemOrCategoryId}`;
                        }
                        
                        return (
                          <div className="d-flex align-items-center justify-content-center">
                            <div
                              className="me-2 p-1 rounded"
                              style={{
                                backgroundColor: isDarkMode ? '#374151' : '#f0f0f0',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <TagsOutlined style={{ color: themeColor }} />
                            </div>
                            <div className="fw-semibold">
                              {name}
                            </div>
                          </div>
                        );
                      },
                    },
                    {
                      title: <div className="text-center">Points Per Item</div>,
                      dataIndex: "PointsPerItem",
                      key: "points",
                      align: 'center',
                      render: (points, record) => (
                        <div className="text-center">
                          <Tag
                            color="blue"
                            className="fw-semibold px-3 py-1"
                            style={{ fontSize: '14px' }}
                          >
                            {points !== undefined ? points : (record.pointsPerItem !== undefined ? record.pointsPerItem : 1)} pts
                          </Tag>
                        </div>
                      ),
                    },
                    {
                      title: <div className="text-center">Expiry Days</div>,
                      dataIndex: "ExpiryDays",
                      key: "expiry",
                      align: 'center',
                      render: (days, record) => {
                        const expiryDays = days !== undefined ? days : (record.expiryDays !== undefined ? record.expiryDays : null);
                        return (
                          <div className="text-center">
                            {expiryDays && expiryDays > 0 ? (
                              <Tag color="orange" className="px-3 py-1 fw-semibold">
                                {expiryDays} {expiryDays === 1 ? 'day' : 'days'}
                              </Tag>
                            ) : (
                              <Tag color="green" className="px-3 py-1">
                                <ClockCircleOutlined className="me-1" />
                                Never Expires
                              </Tag>
                            )}
                          </div>
                        );
                      },
                    },
                  ]}
                  dataSource={ruleConditions}
                  rowKey={(record) => record.Condition_ID || record.condition_ID || record.ItemORCategoryID || record.itemORCategoryID}
                  pagination={false}
                  size="middle"
                  className={isDarkMode ? 'dark-table' : ''}
                />
              </Card>
            </div>
          </div>
        )}

        {/* Rule Description */}
        <div className="row">
          <div className="col-12">
            <Card
              className="border-0 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <div className="card-body p-4">
                <h6 className="mb-3">Rule Description</h6>
                <div className="p-3 rounded" style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#f8f9fa',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e9ecef',
                }}>
                  {currentRuleType === 'AMOUNT' && (
                    <p className="mb-0">
                      Customers earn <strong>{rewardPoints} points</strong> for every <strong>{currencySymbol}{spendAmount}</strong> spent.
                      {expirationDays && <span> Points expire after <strong>{expirationDays} days</strong>.</span>}
                    </p>
                  )}
                  {currentRuleType === 'ORDER' && (
                    <p className="mb-0">
                      Customers earn <strong>{rewardPoints} points</strong> per order.
                      {expirationDays && <span> Points expire after <strong>{expirationDays} days</strong>.</span>}
                    </p>
                  )}
                  {currentRuleType === 'ITEM' && (
                    <p className="mb-0">
                      Customers earn points for purchasing specific items. Each item has its own point value and expiration settings.
                      {ruleConditions.length > 0 && <span> Currently configured for <strong>{ruleConditions.length}</strong> item(s).</span>}
                    </p>
                  )}
                  {currentRuleType === 'CATEGORY' && (
                    <p className="mb-0">
                      Customers earn points for purchasing items from specific categories. Each category has its own point value and expiration settings.
                      {ruleConditions.length > 0 && <span> Currently configured for <strong>{ruleConditions.length}</strong> category/categories.</span>}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const renderConditionsTable = () => {
    if (!rule || (rule.RuleType !== 'ITEM' && rule.RuleType !== 'CATEGORY')) return null;

    return (
      <div className="row mb-4">
        <div className="col-12">
          <Card
            title={
              <div className="d-flex align-items-center justify-content-between">
                <span className="d-flex align-items-center">
                  <TagsOutlined className="me-2" />
                  {rule.RuleType === 'ITEM' ? 'Item Conditions' : 'Category Conditions'}
                </span>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddCondition}
                  style={{
                    backgroundColor: themeColor,
                    borderColor: themeColor,
                  }}
                >
                  Add Condition
                </Button>
              </div>
            }
            className="border-0 shadow-sm"
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            <Table
              columns={conditionColumns}
              dataSource={conditions}
              rowKey="Condition_ID"
              pagination={false}
              size="middle"
              className={isDarkMode ? 'dark-table' : ''}
            />
          </Card>
        </div>
      </div>
    );
  };


  const pageWrapperStyle = isDarkMode ? { color: '#e2e8f0' } : undefined;
  const cardDarkStyle = isDarkMode ? { backgroundColor: '#2d3436', border: '1px solid #404040', color: '#ffffff' } : undefined;

  const selectedRuleTypeMeta = LOYALTY_RULE_TYPES.find((t) => t.value === ruleType);
  const previewItemCount = selectors.selectedItems.length;
  const previewCategoryCount = selectors.selectedCategories.length;

  const getRulePreviewText = () => {
    if (!ruleType) {
      return "Select a rule type to see how points will be earned.";
    }
    if (ruleType === "AMOUNT") {
      const amount = watchedSpendAmount || "—";
      const points = watchedRewardPoints || "—";
      return `Customers earn ${points} point${watchedRewardPoints === 1 ? "" : "s"} for every ${currencySymbol}${amount} spent.`;
    }
    if (ruleType === "ORDER") {
      const points = watchedRewardPoints || "—";
      return `Customers earn ${points} point${watchedRewardPoints === 1 ? "" : "s"} for every completed order.`;
    }
    if (ruleType === "ITEM") {
      if (previewItemCount === 0) {
        return "Select products or variants and set points for each item.";
      }
      return `Points are awarded for ${previewItemCount} selected item${previewItemCount === 1 ? "" : "s"}.`;
    }
    if (ruleType === "CATEGORY") {
      if (previewCategoryCount === 0) {
        return "Select categories and set points earned per matching purchase.";
      }
      return `Points are awarded for items in ${previewCategoryCount} selected categor${previewCategoryCount === 1 ? "y" : "ies"}.`;
    }
    return "";
  };

  const renderRuleTypePicker = () => (
    <div className="loyalty-rule-type-form-item mb-0">
      <div
        className={`loyalty-rule-type-grid ${isDarkMode ? "loyalty-rule-type-grid--dark" : ""}`}
        style={{ "--loyalty-accent": themeColor }}
        role="radiogroup"
        aria-label="Loyalty rule type"
      >
        {LOYALTY_RULE_TYPES.map(({ value, title, description, Icon }) => {
          const isSelected = ruleType === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`loyalty-rule-type-card ${isSelected ? "loyalty-rule-type-card--selected" : ""}`}
              onClick={() => handleRuleTypeChange(value)}
            >
              <span className="loyalty-rule-type-card__icon">
                <Icon />
              </span>
              <span className="loyalty-rule-type-card__body">
                <span className="loyalty-rule-type-card__title">{title}</span>
                <span className="loyalty-rule-type-card__desc">{description}</span>
              </span>
              {isSelected && (
                <CheckCircleOutlined className="loyalty-rule-type-card__check" />
              )}
            </button>
          );
        })}
      </div>
      <Form.Item
        name="RuleType"
        rules={[{ required: true, message: "Please select a rule type" }]}
        className="mb-0 loyalty-rule-type-hidden-field"
      >
        <Select aria-hidden="true" tabIndex={-1}>
          {LOYALTY_RULE_TYPES.map(({ value, title }) => (
            <Option key={value} value={value}>
              {title}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );

  const renderRewardFields = () => {
    if (!ruleType || (ruleType !== "AMOUNT" && ruleType !== "ORDER")) {
      return null;
    }

    return (
      <div className="loyalty-config-fields">
        {ruleType === "AMOUNT" && (
          <Form.Item
            name="SpendAmount"
            label={`Base amount (${currencySymbol})`}
            extra="Customers earn points for every multiple of this amount spent."
            rules={[{ required: true, message: "Please enter base amount" }]}
          >
            <InputNumber
              min={0.01}
              step={0.01}
              placeholder="1.00"
              size="large"
              className="w-100 loyalty-config-input"
              addonBefore={currencySymbol}
            />
          </Form.Item>
        )}

        {ruleType === "ORDER" && (
          <Form.Item name="SpendAmount" hidden>
            <InputNumber value={1} />
          </Form.Item>
        )}

        <Form.Item
          name="RewardPoints"
          label={ruleType === "ORDER" ? "Points per order" : "Reward points"}
          extra={
            ruleType === "ORDER"
              ? "Fixed points awarded each time an order is completed."
              : "Points awarded for each base amount spent."
          }
          rules={[{ required: true, message: "Please enter reward points" }]}
        >
          <InputNumber
            min={0.01}
            step={0.01}
            placeholder="1"
            size="large"
            className="w-100 loyalty-config-input"
          />
        </Form.Item>

        <Form.Item
          name="ExpirationDays"
          label="Point expiration (days)"
          extra="Leave empty if points should never expire."
          tooltip="Number of days before earned points expire."
        >
          <InputNumber
            min={1}
            placeholder="Never expires"
            size="large"
            className="w-100 loyalty-config-input"
          />
        </Form.Item>
      </div>
    );
  };

  const renderStatusPanel = () => (
    <div
      className={`loyalty-status-panel ${isDarkMode ? "loyalty-status-panel--dark" : ""}`}
      style={{ "--loyalty-accent": themeColor }}
    >
      <div className="loyalty-status-panel__header">
        <CheckCircleOutlined />
        <div>
          <h5 className="mb-0">Status &amp; preview</h5>
          <small>Control whether this rule is live</small>
        </div>
      </div>

      <Form.Item name="Status" label="Rule status" valuePropName="checked" className="mb-3">
        <div className="loyalty-status-toggle-row">
          <ToggleSwitch
            isChecked={toggles.Status}
            onToggle={handleToggle("Status")}
            label={
              toggles.Status ? (
                <span className="text-success">
                  <CheckCircleOutlined className="me-1" />
                  Active
                </span>
              ) : (
                <span className="text-muted">
                  <StopOutlined className="me-1" />
                  Inactive
                </span>
              )
            }
            name="Status"
            themeColor={themeColor}
          />
        </div>
      </Form.Item>

      <div className="loyalty-status-preview">
        <div className="loyalty-status-preview__label">
          <InfoCircleOutlined className="me-1" />
          How it works
        </div>
        <p className="loyalty-status-preview__text mb-2">{getRulePreviewText()}</p>
        {ruleType && (
          <div className="loyalty-status-preview__meta">
            <span className="loyalty-status-chip">
              {selectedRuleTypeMeta?.title || ruleType}
            </span>
            {watchedExpirationDays ? (
              <span className="loyalty-status-chip loyalty-status-chip--muted">
                <ClockCircleOutlined className="me-1" />
                Expires in {watchedExpirationDays} days
              </span>
            ) : ruleType === "AMOUNT" || ruleType === "ORDER" ? (
              <span className="loyalty-status-chip loyalty-status-chip--muted">
                No expiration
              </span>
            ) : null}
            {!toggles.Status && (
              <span className="loyalty-status-chip loyalty-status-chip--warn">
                Not earning yet
              </span>
            )}
          </div>
        )}
      </div>

      {!ruleType && (
        <div className="loyalty-status-hint">
          Tip: Start with Amount Based if you want a simple spend-to-points rule.
        </div>
      )}
    </div>
  );

  return (
    <div className="loyalty-rules-page" style={pageWrapperStyle}>
      {renderBreadcrumb()}
      <GridSectionLoader loading={loading} minHeight="320px">
      {!isEditMode ? (
        renderRuleView()
      ) : (
        <>
          {renderConditionsTable()}
          <div className="card border-0" style={cardDarkStyle}>
        <Panel className="m-3">
          <PanelBody>
            <div
              className={`loyalty-config-shell ${isDarkMode ? "loyalty-config-shell--dark" : ""}`}
              style={{ "--loyalty-accent": themeColor }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={() => handleSubmit({ exitAfterSave: true })}
                initialValues={{
                  Status: true,
                  RuleType: rule?.RuleType,
                  SpendAmount: rule?.SpendAmount,
                  RewardPoints: rule?.RewardPoints,
                  ExpirationDays: rule?.ExpirationDays,
                  Status: rule?.Status ?? true,
                }}
              >
                <div className="loyalty-config-intro">
                  <h5 className="loyalty-config-intro__title">
                    {rule ? "Update your loyalty rule" : "Set up how customers earn points"}
                  </h5>
                  <p className="loyalty-config-intro__text mb-0">
                    Choose an earning method, configure the reward values, then activate the rule when you are ready.
                  </p>
                </div>

                <div className="loyalty-config-section">
                  <div className="loyalty-config-section__header">
                    <span className="loyalty-config-step">1</span>
                    <div>
                      <h6 className="mb-0">Choose earning method</h6>
                      <small className="text-muted">Pick one rule type for your loyalty program</small>
                    </div>
                  </div>
                  {renderRuleTypePicker()}
                </div>

                <div className="row g-4 loyalty-config-details-row">
                  <div className="col-lg-8">
                    <div
                      className={`loyalty-config-main ${isDarkMode ? "loyalty-config-main--dark" : ""}`}
                    >
                      <div className="loyalty-config-section__header mb-3">
                        <span className="loyalty-config-step">2</span>
                        <div>
                          <h6 className="mb-0">Configure rewards</h6>
                          <small className="text-muted">
                            {ruleType
                              ? selectedRuleTypeMeta?.description
                              : "Select a rule type above to continue"}
                          </small>
                        </div>
                      </div>

                      {!ruleType && (
                        <div className="loyalty-config-awaiting">
                          <SettingOutlined />
                          <div>
                            <strong>Waiting for rule type</strong>
                            <p className="mb-0">
                              Once you select how points are earned, the reward settings will appear here.
                            </p>
                          </div>
                        </div>
                      )}

                      {renderRewardFields()}

                      {ruleType === "ITEM" && renderSelectedItemsEditor()}
                      {ruleType === "CATEGORY" && renderSelectedCategoriesEditor()}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    {renderStatusPanel()}
                  </div>
                </div>
              </Form>
            </div>
          </PanelBody>
        </Panel>
      </div>
 


      {/* Category Selection Modal */}
      <CategorySelector
        selectedCategories={selectors.selectedCategories}
        setSelectedCategories={(updater) =>
          setSelectors((prev) => {
            const currentCats = prev.selectedCategories;
            const newCats =
              typeof updater === "function"
                ? updater(currentCats)
                : updater;
            return { ...prev, selectedCategories: newCats.map(cat => ({
              ...cat,
              pointsPerItem: cat.pointsPerItem || 1,
              expiryDays: cat.expiryDays || null,
            })) };
          })
        }
        showModal={modals.showCategoryModal}
        setShowModal={(val) =>
          val ? openModal('showCategoryModal') : closeAllModals()
        }
        onSubmit={handleSelectorSubmit("selectedCategories")}
        setLoading={setLoading}
        hideDisplay={true}
      />

      {/* Item Selection Modal */}
      <ProductVariantMultiSelectionModal
        selectedProducts={selectors.selectedItems}
        setSelectedProducts={(products) =>
          setSelectors((prev) => ({
            ...prev,
            selectedItems: (products || []).map((p) => ({
              ...p,
              pointsPerItem: p.pointsPerItem || 1,
              expiryDays: p.expiryDays ?? null,
            })),
          }))
        }
        showModal={modals.showItemModal}
        setShowModal={(val) =>
          val ? openModal('showItemModal') : closeAllModals()
        }
        onSubmit={(selected) => {
          mergeItemsCache(selected || []);
          handleSelectorSubmit("selectedItems")(selected);
        }}
        compID={CompID}
        showButton={true}
      />
        </>
      )}
      </GridSectionLoader>
    </div>
  );
};

export default LoyaltyRulesManagement;
